import { useEffect, useState } from "react";
import { AccordionHeader } from "../accordionHeader/AccordionHeader";
import { useFormContext } from "react-hook-form";
import { BEErrorCodes } from "../../helpers/constants/errorCodes";
import { ErrorWarningWrapper } from "./ErrorWarning.style";
import { useSiteCore } from "../../hooks/useSiteCore";
import { getParsedBEErrors, hasKey, isNotEmpty } from "../../helpers/utils";
import { MuiTypography } from "../typography/MuiTypography";
import { MuiBox } from "../box/MuiBox";
import { BUSINESS_IDENTIFIERS as BusinessIdentifiers } from "../../helpers/constants";
import { ErrorModel } from "../../api/services/client/models";

interface ErrorWarningProps {
  error?: any;
  localErrorMessage?: string;
}
const ErrorWarning = ({ error, localErrorMessage }: ErrorWarningProps) => {
  const { setError, formState, getValues } = useFormContext();
  const { fetchBEValidationMessage, commonLabels } = useSiteCore();
  const [BEError, setBEError] = useState<ErrorModel[] | []>([]);

  type BusinessIdentifierKeys = keyof typeof BusinessIdentifiers;

  const getTouchedBusinessIdentifier = (touchedFields: {
    [key in BusinessIdentifierKeys]?: any;
  }) => {
    const keys = Object.keys(BusinessIdentifiers) as BusinessIdentifierKeys[];

    for (const key of keys) {
      if (touchedFields[key]) {
        return { key: key, name: BusinessIdentifiers[key] };
      }
    }

    return {};
  };

  const getBusinessIdentifierFromValues = () => {
    const keys = Object.keys(BusinessIdentifiers) as BusinessIdentifierKeys[];
    const values = getValues();

    for (const key of keys) {
      if (values[key] && isNotEmpty(values[key])) {
        return { key: key, name: BusinessIdentifiers[key] };
      }
    }

    return {};
  };

  const renderHeader = () => (
    <MuiBox className="error-warning-header-wrapper">
      <MuiTypography variant="h2SemiBold" className="error-warning-header">
        {commonLabels.errorsIdentified}
      </MuiTypography>
      <MuiTypography variant="h3Bold" className="error-warning-header-badge">
        {BEError.length > 0 ? BEError.length : 1}
      </MuiTypography>
    </MuiBox>
  );

  const getIdentifierName = () => {
    const touchedIdentifier = getTouchedBusinessIdentifier(
      formState.touchedFields,
    );

    if (isNotEmpty(touchedIdentifier)) {
      return touchedIdentifier?.name ?? "";
    } else {
      // Fallback to checking actual form values if no field was touched
      const valueIdentifier = getBusinessIdentifierFromValues();
      return valueIdentifier?.name ?? "";
    }
  };

  const hasBEErrorCode = (item: ErrorModel) => {
    return BEErrorCodes.hasOwnProperty(item.errorCode);
  };

  const formatErrorMessage = (item: ErrorModel, index: number) => {
    let fieldName = "";
    if (hasBEErrorCode(item)) {
      fieldName =
        BEErrorCodes[Number(item.errorCode)].fieldKey === "bin"
          ? getIdentifierName()
          : (BEErrorCodes[Number(item.errorCode)].fieldName ?? "");
    }
    const message = getErrorMessage(item);

    let result = `${index + 1}. `;

    if (isNotEmpty(fieldName)) {
      result += `${fieldName} : `;
    }

    result += message;
    return result;
  };

  const getErrorMessage = (item: ErrorModel) => {
    const errorMessage = fetchBEValidationMessage(
      item.errorCode.toString(),
      getIdentifierName(),
    );
    return isNotEmpty(errorMessage) ? errorMessage : item.message;
  };

  useEffect(() => {
    if (error) {
      setBEError(getParsedBEErrors(error));
    }
  }, [error]);

  const isValidBEError = () => {
    return (
      isNotEmpty(BEError) &&
      Array.isArray(BEError) &&
      hasKey(BEError[0], "errorCode")
    );
  };

  useEffect(() => {
    if (isValidBEError()) {
      BEError.forEach((item: ErrorModel) => {
        if (hasBEErrorCode(item)) {
          setBEFieldError(item);
        }
      });
    }
  }, [BEError]);

  const setBEFieldError = (item: ErrorModel) => {
    const message = getErrorMessage(item);
    const fieldKey = BEErrorCodes[Number(item.errorCode)].fieldKey;

    if (fieldKey === "bin") {
      let identifier = getTouchedBusinessIdentifier(formState.touchedFields);

      // If no touched identifier, check actual form values
      if (!identifier?.key) {
        identifier = getBusinessIdentifierFromValues();
      }

      const { key: binKey, name } = identifier || {};

      if (binKey && name) {
        setError(binKey, { type: "custom", message: message });
      }
    } else {
      setError(fieldKey, { type: "custom", message });
    }
  };

  return (
    <>
      {(isValidBEError() || isNotEmpty(localErrorMessage)) && (
        <ErrorWarningWrapper>
          <AccordionHeader title={renderHeader()} isExpanded={true}>
            <MuiBox className="error-warning">
              {isNotEmpty(localErrorMessage) ? (
                <MuiBox className="error-warning-list">
                  {`1. ${localErrorMessage}`}
                </MuiBox>
              ) : (
                BEError.map((item, index) => (
                  <MuiBox key={item.errorCode} className="error-warning-list">
                    {formatErrorMessage(item, index)}
                  </MuiBox>
                ))
              )}
            </MuiBox>
          </AccordionHeader>
        </ErrorWarningWrapper>
      )}
    </>
  );
};

export default ErrorWarning;
